/**
 * Shared Zod schemas for Frontend and Backend.
 * Security Checklist: Frontend and Backend MUST use the EXACT same schema.
 * Single source of truth for API request/response validation and form validation.
 */

import { z } from 'zod';

// ─── Shared primitives ─────────────────────────────────────────────────────

export const emailSchema = z
  .string()
  .min(1, 'auth.validation.emailRequired')
  .email('auth.validation.emailInvalid')
  .transform((v) => v.trim());

// ─── Auth: Register (form: mobile; API: phone) ─────────────────────────────

/** Mobile stored as digits only (9 digits, Saudi: starts with 5). Form field. */
export const registerFormSchema = z.object({
  mobile: z.string().min(1, 'auth.validation.mobileRequired').refine(
    (v) => /^5[0-9]{8}$/.test((v ?? '').replace(/\D/g, '')),
    'auth.validation.mobileInvalid',
  ),
});
export type RegisterFormValues = z.infer<typeof registerFormSchema>;

/** Who is logging in via OTP: broker platform (default) vs client/payer flow (requires pending deal). */
export const authOtpContextSchema = z.enum(['broker', 'client']);

/** API body for POST /api/auth/register – use "phone" (normalized from form mobile). */
export const authRegisterBodySchema = z.object({
  phone: z.string().min(1).refine(
    (v) => /^5[0-9]{8}$/.test((v ?? '').replace(/\D/g, '')),
    'Invalid Saudi mobile',
  ),
  /** Omit or `broker`: broker/admin OTP. `client`: phone must be on a Pending deal (payer flow). */
  authContext: authOtpContextSchema.optional().default('broker'),
});

// ─── Auth: OTP ──────────────────────────────────────────────────────────────

export const otpFormSchema = z.object({
  code: z.string().min(1, 'auth.validation.verificationRequired').regex(/^[0-9]{6}$/, 'auth.validation.verificationInvalid'),
});
export type OtpFormValues = z.infer<typeof otpFormSchema>;

/** API body for POST /api/auth/verify-otp */
export const authVerifyOtpBodySchema = z.object({
  phone: z.string().min(1),
  code: z.string().length(6).regex(/^[0-9]+$/),
  authContext: authOtpContextSchema.optional().default('broker'),
});

// ─── Auth: Sign-up complete (ID, DOB, email) ────────────────────────────────

/** Saudi national ID Luhn (double 0-based even indices; subtract 9 if > 9). */
export function saudiNationalIdChecksumValid(id: string): boolean {
  if (!id || id.length !== 10) return false;
  let total = 0;
  for (let i = 0; i < 10; i++) {
    let d = parseInt(id[i]!, 10);
    if (Number.isNaN(d)) return false;
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  return total % 10 === 0;
}

/** Strict 10-digit Saudi citizen national ID (leading 1 only; rejects Iqama 2). */
export const saudiNationalIdSchema = z
  .string()
  .min(1, 'auth.validation.nationalIdRequired')
  .length(10, 'auth.validation.nationalIdLength')
  .regex(/^[0-9]+$/, 'auth.validation.nationalIdDigits')
  .refine((v) => v[0] !== '2', { message: 'auth.validation.nationalIdSaudiOnly' })
  .refine((v) => v[0] === '1', { message: 'auth.validation.nationalIdFormat' })
  .refine(saudiNationalIdChecksumValid, { message: 'auth.validation.nationalIdChecksum' });

/** Bypass variant — accepts any non-empty string (pre-production testing only). */
export const looseSaudiNationalIdSchema = z.string().min(1, 'auth.validation.nationalIdRequired');

export const signUpCompleteFormSchema = z.object({
  idNumber: saudiNationalIdSchema,
  dob: z.string().min(10, 'auth.validation.dobRequired').refine(
    (v) => {
      const d = new Date(v + 'T12:00:00');
      if (Number.isNaN(d.getTime())) return false;
      return d <= new Date();
    },
    'auth.validation.dobInvalid',
  ),
  email: emailSchema,
});
export type SignUpCompleteFormValues = z.infer<typeof signUpCompleteFormSchema>;

/** API body for POST /api/auth/complete */
export const authCompleteBodySchema = z.object({
  nationalId: saudiNationalIdSchema,
  dateOfBirth: z.string().min(10),
  email: emailSchema,
});

export const authNafathInitiateResponseSchema = z.object({
  transId: z.string(),
  random: z.string(),
});

export const nafathVerificationStatusSchema = z.enum(['PENDING', 'COMPLETED', 'REJECTED', 'EXPIRED']);

export const authNafathStatusResponseSchema = z.object({
  status: nafathVerificationStatusSchema,
  fullName: z.string().optional(),
  email: z.string().optional(),
  nationalAddresses: z.array(z.object({
    addressId: z.string(),
    shortAddress: z.string().nullable().optional(),
    fullAddress: z.string().nullable().optional(),
  })).optional(),
});

export const authNationalAddressBodySchema = z.object({
  addressId: z.string().min(1),
  fullAddress: z.string().optional(),
});

// ─── Admin: Invite account (POST /api/admin/accounts) ───────────────────────

const adminAccountRoleEnum = z.enum(['system_administrator', 'operations_manager', 'financial_officer', 'support_agent']);

export const inviteAdminFormSchema = z.object({
  name: z.string().min(1, 'admin.accounts.nameRequired').trim(),
  email: emailSchema,
  mobile: z.string().optional(),
  role: adminAccountRoleEnum,
  parentId: z.string().nullable().optional(),
});
export type InviteAdminFormValues = z.infer<typeof inviteAdminFormSchema>;

export const createAdminAccountBodySchema = z.object({
  name: z.string().min(1).trim(),
  email: z.string().min(1).email().trim(),
  mobile: z.string().optional(),
  role: adminAccountRoleEnum,
  parentId: z.string().nullable().optional(),
});
export type CreateAdminAccountBody = z.infer<typeof createAdminAccountBodySchema>;

export const updateAdminAccountBodySchema = z.object({
  role: adminAccountRoleEnum.optional(),
  parentId: z.string().nullable().optional(),
});
export type UpdateAdminAccountBody = z.infer<typeof updateAdminAccountBodySchema>;

export const updateAdminSettingsBodySchema = z.object({
  platformName: z.string().optional(),
  defaultLanguage: z.string().optional(),
  saeeCommissionRate: z.number().min(0).max(100).optional(),
});
export type UpdateAdminSettingsBody = z.infer<typeof updateAdminSettingsBodySchema>;

// ─── Admin: Create broker (POST /api/brokers) ───────────────────────────────

export const createBrokerBodySchema = z.object({
  name: z.string().min(1, 'admin.brokerForm.nameRequired').trim(),
  mobile: z
    .string()
    .min(1, 'admin.brokerForm.mobileRequired')
    .transform((v) => (v ?? '').replace(/\D/g, '').slice(-9))
    .refine((digits) => /^5[0-9]{8}$/.test(digits), 'auth.validation.mobileInvalid'),
  nationalId: saudiNationalIdSchema,
  dateOfBirth: z
    .string()
    .min(10, 'auth.validation.dobRequired')
    .refine(
      (v) => {
        const d = new Date(v + 'T12:00:00');
        if (Number.isNaN(d.getTime())) return false;
        return d <= new Date();
      },
      'auth.validation.dobInvalid',
    ),
  email: emailSchema,
  falLicenseNumber: z.string().optional().nullable(),
  iban: z.string().optional().nullable(),
});
export type CreateBrokerBody = z.infer<typeof createBrokerBodySchema>;

export const brokerInternalNoteBodySchema = z.object({
  body: z.string().trim().min(1, 'auth.validation.fieldRequired').max(5000),
});
export type BrokerInternalNoteBody = z.infer<typeof brokerInternalNoteBodySchema>;

// ─── Admin: Create deal (POST /api/deals) ───────────────────────────────────

export const createDealBrokerEntrySchema = z.object({
  phone: z.string().min(1),
  rate: z.number().min(0).max(100),
});

export const createDealAttachmentManifestEntrySchema = z.object({
  fileName: z.string().min(1).max(255),
  sizeBytes: z.number().int().min(0).max(100_000_000),
});

export const createDealBodySchema = z
  .object({
    commissionType: z.string(),
    commissionRate: z.number(),
    clientRole: z.string(),
    propertyName: z.string().min(1),
    // Optional — geo fields may be absent when the geo API is unavailable.
    // Server-side validation enforces completeness where required.
    city: z.string().optional().default(''),
    district: z.string().optional().default(''),
    propertyValue: z.number().min(0),
    deedNumber: z.string(),
    propertyDescription: z.string(),
    brokers: z.array(createDealBrokerEntrySchema).min(1),
    clientName: z.string().min(1),
    clientPhone: z.string().min(1),
    contractAgreed: z.boolean(),
    /** Staff-only: broker user id to create the deal as lead (POST /api/deals from backoffice). */
    leadBrokerId: z.string().uuid().optional(),
    /** Optional metadata for contract files selected in the wizard (names + sizes). */
    attachmentManifest: z.array(createDealAttachmentManifestEntrySchema).max(25).optional(),
  })
  .refine((data) => data.contractAgreed === true, { message: 'admin.dealsForm.contractRequired', path: ['contractAgreed'] });
export type CreateDealBody = z.infer<typeof createDealBodySchema>;
export type CreateDealBrokerEntry = z.infer<typeof createDealBrokerEntrySchema>;

export const addDealBrokerBodySchema = z
  .object({
    brokerId: z.string().optional(),
    phone: z.string().optional(),
    ratePercent: z.number().min(0).max(100),
  })
  .refine((d) => d.brokerId != null || (d.phone != null && d.phone.length > 0), {
    message: 'Either brokerId or phone required',
    path: ['brokerId'],
  });
export type AddDealBrokerBody = z.infer<typeof addDealBrokerBodySchema>;

export const updateDealBrokerBodySchema = z.object({
  ratePercent: z.number().min(0).max(100),
});
export type UpdateDealBrokerBody = z.infer<typeof updateDealBrokerBodySchema>;

/** API body for POST /api/deals/:id/request-refund */
export const requestRefundBodySchema = z.object({
  reason: z.string().min(1),
  details: z.string().optional(),
});
export type RequestRefundBody = z.infer<typeof requestRefundBodySchema>;

/** API body for PATCH /api/deals/:id/cancel */
export const cancelDealBodySchema = z.object({
  reason: z.string().min(1),
});
export type CancelDealBody = z.infer<typeof cancelDealBodySchema>;

/** API body for POST /api/deals/:id/refund/reject – reason + optional message for admin disputed case */
export const rejectRefundBodySchema = z.object({
  reason: z.string().min(1),
  message: z.string().optional(),
});
export type RejectRefundBody = z.infer<typeof rejectRefundBodySchema>;

/** API body for POST /api/deals/:id/brokers/:brokerId/approve – OTP to confirm approval */
export const brokerApproveBodySchema = z.object({
  code: z.string().min(6, 'Code must be 6 digits').max(6).regex(/^[0-9]+$/),
});
export type BrokerApproveBody = z.infer<typeof brokerApproveBodySchema>;

/** API body for POST /api/deals/:id/refund/approve – OTP after POST .../refund/request-approve/ */
export const refundApproveBodySchema = brokerApproveBodySchema;
export type RefundApproveBody = z.infer<typeof refundApproveBodySchema>;

/** API body for POST /api/deals/:id/brokers/:brokerId/reject – dispute reason + message to lead broker */
export const brokerRejectBodySchema = z.object({
  reason: z.string().min(1),
  message: z.string().optional(),
});
export type BrokerRejectBody = z.infer<typeof brokerRejectBodySchema>;

// ─── User Profile ─────────────────────────────────────────────────────────

export const userProfileDisplaySchema = z.object({
  name: z.string(),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string(),
});
export type UserProfileDisplay = z.infer<typeof userProfileDisplaySchema>;

export const userProfileEmailUpdateSchema = z.object({
  email: emailSchema,
});
export type UserProfileEmailUpdate = z.infer<typeof userProfileEmailUpdateSchema>;

/** API body for PATCH /api/profile */
export const profilePatchBodySchema = z.object({
  email: emailSchema.optional(),
  name: z.string().trim().optional(),
});

/** Max avatar payload ~700KB base64 (for ~500KB image). */
const AVATAR_BASE64_MAX_LENGTH = 700_000;

/** API body for POST /api/profile/avatar – base64 data URL (image/jpeg or image/png). */
export const profileAvatarBodySchema = z.object({
  avatar: z
    .string()
    .min(1)
    .refine(
      (v) => v.startsWith('data:image/jpeg;base64,') || v.startsWith('data:image/png;base64,'),
      'Avatar must be JPEG or PNG',
    )
    .refine((v) => v.length <= AVATAR_BASE64_MAX_LENGTH, 'Avatar image too large'),
});

export const ibanFormSchema = z.object({
  iban: z
    .string()
    .min(1, 'validation.ibanRequired')
    .transform((v) => (v ?? '').trim().toUpperCase())
    .refine((v) => v.startsWith('SA'), 'validation.ibanMustStartWithSA')
    .refine((v) => v.length === 24, 'validation.ibanValidSaudi')
    .refine((v) => /^SA[0-9]{22}$/.test(v), 'validation.ibanMustBe24'),
});
export type IbanFormValues = z.infer<typeof ibanFormSchema>;

/** API body for PATCH /api/profile/iban and POST /api/wallet/iban */
export const ibanBodySchema = z.object({
  iban: z.string().trim().toUpperCase().refine((v) => /^SA[0-9]{22}$/.test(v)),
});

export const falLicenseFormSchema = z.object({
  falLicense: z.string().transform((v) => (v ?? '').trim()).pipe(z.string().min(1, 'validation.falRequired')),
});
export type FalLicenseFormValues = z.infer<typeof falLicenseFormSchema>;

/** API body for POST /api/profile/fal */
export const profileFalBodySchema = z.object({
  falLicenseNumber: z.string().min(1).trim(),
});

// ─── New Brokerage Request ─────────────────────────────────────────────────

/** Broker form field: 9 digits starting with 5 (optional leading 0 or 966). */
function isValidSaudiBrokerPhoneInput(phone: string): boolean {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (!digits) return false;
  const normalized = digits.startsWith('966')
    ? digits.slice(3)
    : digits.startsWith('0')
      ? digits.slice(1)
      : digits;
  return /^[0-9]{9}$/.test(normalized) && normalized[0] === '5';
}

const newBrokerageBrokerEntrySchema = z.object({
  phone: z
    .string()
    .min(1, 'newBrokerage.validation.brokerPhoneRequired')
    .refine(isValidSaudiBrokerPhoneInput, 'auth.validation.mobileInvalid'),
  rate: z
    .union([z.number(), z.string()])
    .transform((v) => (typeof v === 'string' ? parseFloat(v.replace(/,/g, '')) : v))
    .pipe(z.number().min(0, 'newBrokerage.validation.brokerRateRequired').max(100, 'newBrokerage.validation.brokerRateInvalid')),
});

export const newBrokerageStep1Schema = z
  .object({
    commissionType: z.string(),
    commissionRate: z
      .union([z.number(), z.string()])
      .transform((v) => (typeof v === 'string' ? parseFloat(String(v).replace(/,/g, '')) : v))
      .pipe(z.number().min(0, 'newBrokerage.validation.rateRequired')),
    clientRole: z.string(),
    propertyName: z.string().min(1, 'newBrokerage.validation.fieldRequired'),
    // city and district are optional at the step level so the wizard can advance
    // without a geo-combo selection; the full newBrokerageRequestSchema enforces
    // them as required before final submission.
    city: z.string().optional().default(''),
    district: z.string().optional().default(''),
    propertyValue: z
      .union([z.number(), z.string()])
      .transform((v) => (typeof v === 'string' ? parseFloat(String(v).replace(/,/g, '')) : v))
      .pipe(z.number().min(0, 'newBrokerage.validation.propertyValueInvalid')),
    deedNumber: z.string().min(1, 'newBrokerage.validation.deedRequired'),
    propertyDescription: z.string(),
  })
  .refine(
    (d) => d.commissionType !== 'percentage' || (d.commissionRate >= 0 && d.commissionRate <= 100),
    { message: 'newBrokerage.validation.rateInvalidPercentage', path: ['commissionRate'] },
  );

export const newBrokerageStep2Schema = z
  .object({
    brokers: z.array(newBrokerageBrokerEntrySchema).min(0),
  })
  .superRefine((data, ctx) => {
    const sum = data.brokers.reduce((s, b) => {
      const r = typeof b.rate === 'number' ? b.rate : parseFloat(String(b.rate));
      return s + (Number.isFinite(r) ? r : 0);
    }, 0);
    if (sum > 100) {
      ctx.addIssue({ code: 'custom', message: 'newBrokerage.validation.subBrokersExceed100', path: ['brokers'] });
    }
  });

export const newBrokerageStep3Schema = z
  .object({
    clientName: z.string().min(1, 'auth.validation.fieldRequired'),
    clientPhone: z.string().min(1, 'auth.validation.mobileRequired'),
    contractAgreed: z.boolean(),
  })
  .refine((d) => d.contractAgreed === true, { message: 'admin.dealsForm.contractRequired', path: ['contractAgreed'] });

export const newBrokerageRequestSchema = z
  .object({
    commissionType: z.string(),
    commissionRate: z.union([z.number(), z.string()]).transform((v) => (typeof v === 'string' ? parseFloat(String(v).replace(/,/g, '')) : v)).pipe(z.number().min(0)),
    clientRole: z.string(),
    propertyName: z.string().min(1),
    // Optional so the wizard can submit when the geo API is unavailable (e.g. E2E tests
    // without a live backend). The server validates city/district on its end.
    city: z.string().optional().default(''),
    district: z.string().optional().default(''),
    propertyValue: z.union([z.number(), z.string()]).transform((v) => (typeof v === 'string' ? parseFloat(String(v).replace(/,/g, '')) : v)).pipe(z.number().min(0)),
    deedNumber: z.string(),
    propertyDescription: z.string(),
    brokers: z.array(newBrokerageBrokerEntrySchema).min(0),
    clientName: z.string().min(1),
    clientPhone: z.string().min(1),
    contractAgreed: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const sum = data.brokers.reduce((s, b) => {
      const r = typeof b.rate === 'number' ? b.rate : parseFloat(String(b.rate));
      return s + (Number.isFinite(r) ? r : 0);
    }, 0);
    if (sum > 100) {
      ctx.addIssue({ code: 'custom', message: 'newBrokerage.validation.subBrokersExceed100', path: ['brokers'] });
    }
  })
  .refine((data) => data.contractAgreed === true, { message: 'admin.dealsForm.contractRequired', path: ['contractAgreed'] })
  .refine(
    (d) => d.commissionType !== 'percentage' || (d.commissionRate >= 0 && d.commissionRate <= 100),
    { message: 'newBrokerage.validation.rateInvalidPercentage', path: ['commissionRate'] },
  );
export type NewBrokerageRequest = z.infer<typeof newBrokerageRequestSchema>;

export const commissionSplitRowSchema = z.object({
  brokerId: z.string().optional(),
  brokerName: z.string().optional(),
  phone: z.string().optional(),
  ratePercent: z.number().min(0).max(100),
  amountDue: z.number().optional(),
  isNew: z.boolean().optional(),
});
export const commissionSplitRowsSchema = z
  .array(commissionSplitRowSchema)
  .refine((rows) => rows.reduce((s, r) => s + r.ratePercent, 0) <= 100, { message: 'admin.commissionSplitter.sumExceeded' });
export type CommissionSplitRow = z.infer<typeof commissionSplitRowSchema>;
export type CommissionSplitRows = z.infer<typeof commissionSplitRowsSchema>;

export const authAddressSelectionSchema = z.object({
  addressId: z.string().min(1),
});
export type AuthAddressSelection = z.infer<typeof authAddressSelectionSchema>;

// ─── Wallet & Commission logs ──────────────────────────────────────────────

export const walletWithdrawBodySchema = z.object({
  amount: z.number().positive(),
});

export const commissionLogTransferBodySchema = z.object({
  idempotencyKey: z.string().optional(),
});

// ─── Bank webhook (simulator / bank callback) ───────────────────────────────

export const bankCallbackWebhookBodySchema = z.object({
  virtualIban: z.string().trim().refine((v) => /^SA[0-9]{22}$/.test(v), 'Invalid Saudi vIBAN format'),
  amount: z.number().positive(),
  idempotencyKey: z.string().optional(),
});
export type BankCallbackWebhookBody = z.infer<typeof bankCallbackWebhookBodySchema>;

// ─── Deal client-view (GET /api/deals/:id/client-view?token=...) ─────────────

/** Query schema for client-view: token required. */
export const dealClientViewQuerySchema = z.object({
  token: z.string().min(1, 'Token required'),
});

/** Response shape for GET /api/deals/:id/client-view – payer-facing deal summary. */
export const dealClientViewResponseSchema = z.object({
  referenceNumber: z.string(),
  creationDate: z.string(),
  leadBrokerName: z.string(),
  leadBrokerMobile: z.string(),
  participatingBrokers: z.number().int().min(0),
  netCommission: z.number(),
  vat: z.number(),
  total: z.number(),
  commStatus: z.enum(['Paid', 'Unpaid']),
  propertyName: z.string(),
  deedNumber: z.string(),
  city: z.string(),
  district: z.string(),
  propertyValue: z.number(),
  description: z.string(),
  virtualIban: z.string(),
  currency: z.literal('SAR'),
  refundRequested: z.boolean().optional(),
  /** True when lead/admin approved refund (`Deal.status === Refunded`). */
  refunded: z.boolean().optional(),
  /** ISO datetime when the client confirmed property transfer (escrow released); null if not yet. */
  propertyTransferConfirmedAt: z.string().nullable().optional(),
});
export type DealClientViewResponse = z.infer<typeof dealClientViewResponseSchema>;

/** POST /api/deals/:id/payer-refund/confirm?token= */
export const payerRefundConfirmBodySchema = z.object({
  code: z.string().length(6).regex(/^[0-9]+$/),
});
export type PayerRefundConfirmBody = z.infer<typeof payerRefundConfirmBodySchema>;

/** POST /api/admin/system/reset — super_admin only; mirrors backend validation. */
export const platformResetBodySchema = z.object({
  confirm_phrase: z
    .string()
    .trim()
    .refine((s) => s === 'RESET PLATFORM', { message: 'Must type RESET PLATFORM exactly.' }),
  keep_super_admins: z.boolean().optional().default(true),
  /** Required when API runs with DEBUG=false. */
  i_am_sure: z.boolean().optional(),
});
export type PlatformResetBody = z.infer<typeof platformResetBodySchema>;
